<template>
  <BasicConfigurator :value="imagesForBasicConfigurator"
                     :product="product"
                     :configuration-valid="configurationValid"
                     @addedToBasket="addedToBasket"
                     @error="onError"
                     @reset="resetConfiguration">
    <v-stepper v-model="step" vertical :non-linear="true" class="configurationStepper">
      <v-stepper-step v-if="personSelectionNeeded"
                      :complete="step > personSelectionStep"
                      :step="personSelectionStep"
                      edit-icon="check"
                      editable
      >{{ $t('CONFIGURATOR_SELECT_CODE') }}
        <small>{{ $t('CONFIGURATOR_SELECT_PERSON_SUBTITLE') }}</small>
      </v-stepper-step>
      <v-stepper-content v-if="personSelectionNeeded" :step="personSelectionStep">
        <PersonSelector v-model="personIndex" :persons="eligiblePersons" @select="onPersonSelect"/>
      </v-stepper-content>

      <v-stepper-step
          :complete="step > imageSelectionStep"
          :step="imageSelectionStep"
          edit-icon="check"
      >{{ $t('CONFIGURATOR_PREVIEW') }}
      </v-stepper-step>
      <v-stepper-content :step="imageSelectionStep" class="imageStepperContent">
        <ImageSelector :value="selectedImages"
                       :images="selectedImages"
                       :select-all="true"
                       :filter="filter"
                       :scrollable-container-parent-layers="1"
                       ref="imageSelector"
        />
      </v-stepper-content>
    </v-stepper>
  </BasicConfigurator>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {Prop} from "vue-property-decorator";
import SelectableImagePreview from "@/components/SelectableImagePreview.vue";
import {ConfiguredImageDto, ImageDto, ImageFilterDto, ImageRequirementDto, PersonDto, ProductDto} from "@swagger";
import PersonSelector from "@/components/product-configuration/PersonSelector.vue";
import ImageSelector from "@/components/product-configuration/ImageSelector.vue";
import BasicConfigurator from "@/components/product-configuration/BasicConfigurator.vue";

@Component({
  components: {BasicConfigurator, ImageSelector, PersonSelector, SelectableImagePreview}
})
export default class ImageBatchSelector extends BasicComponent {

  @Prop(Array) value: ConfiguredImageDto[];
  @Prop(Object) product: ProductDto;

  /** Optional, if the image is a detailed configuration for a given sub-product */

  step: number = 1;
  personIndex: number|null = null;

  mounted() {
    this.emitChange();
  }

  resetConfiguration() {
    this.personIndex = null;
    this.step = 1;
    this.$emit("input", []);
  }

  onPersonSelect() {
    this.step++;
    setTimeout(() => { // Step expanded
      const imageSelector = this.$refs.imageSelector as ImageSelector;
      imageSelector.onContainerSizeChange();
    }, 1000);
  }

  get requirement(): ImageRequirementDto {
    return this.product.imageRequirements[0];
  }

  get eligiblePersons(): PersonDto[] {
    return this.allPersons.filter(person => {
      const images = this.getPersonImages(person);
      return person.productIds.includes(this.product.id) &&
          images.some(image => this.imageMatchesRequirement(image, this.requirement));
    });
  }

  get personSelectionNeeded(): boolean {
    const persons = this.eligiblePersons;
    // TODO Automatic person selection for friend image products
    return persons && persons.length > 1;
  }

  get personSelectionStep(): number {
    return this.personSelectionNeeded ? 1 : 0;
  }

  get imageSelectionStep(): number {
    return this.personSelectionStep + 1;
  }

  get person(): PersonDto {
    return this.eligiblePersons[this.personIndex ? this.personIndex : 0];
  }

  get selectedImages(): ImageDto[] {
    return this.getPersonImages(this.person).filter(image => this.imageMatchesRequirement(image, this.requirement));
  }

  get filter(): ImageFilterDto {
    return this.product.imageFilter ? this.product.imageFilter : ImageFilterDto.NONE;
  }

  get configuredImages(): ConfiguredImageDto[] {
    return this.selectedImages.map(image => {
      return {
        uniqueImageId: image.uniqueId,
        imageId: image.id,
        imageCropId: image.imageCropId,
        productId: this.product.id,
        filter: this.filter
      };
    });
  }

  get imagesForBasicConfigurator(): ConfiguredImageDto[][] {
    return [this.configuredImages];
  }

  get configurationValid(): boolean {
    return this.step == this.imageSelectionStep && this.eligiblePersons.length > 0 && (!this.personSelectionNeeded || this.personIndex != null);
  }

  emitChange(): void {
    this.$emit("input", this.configuredImages);
  }

  addedToBasket(): void {
    this.$emit("addedToBasket");
  }

  onError(error): void {
    this.$emit("error", error);
  }

}

</script>
<style lang="scss" scoped>
.configurationStepper {
  max-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .imageStepperContent {
    display: flex;
    max-height: 100%;
    flex-direction: column;
    flex: 0 1 auto;
    overflow-y: scroll;
  }
}

.imageLayout {
  padding-top: 5px;
  padding-bottom: 5px;
  align-items: flex-start;

  &:not(:last-of-type) {
    border-bottom: 1px solid #c6c5c5;
  }
}
</style>
<style lang="scss">
.imageStepperContent {
  .v-stepper__wrapper {
    overflow: auto;
  }
}
</style>